import { createContext, useContext } from "react";
import { getPlatformConfig, type PlatformConfig } from "@/lib/platform/config";

/**
 * Platform-config context + hook.
 *
 * Lives in the hooks layer (not in the PlatformProvider component) so that
 * other hooks (e.g. useSupport) can read the platform WITHOUT importing "up"
 * into the components build chunk — an upward import tangles the manualChunks
 * DAG and risks a module-init TDZ white-screen. See scripts/check-import-layering.mjs.
 *
 * IMPORTANT: the context default is `null`, never getPlatformConfig(). Calling
 * it at module-init time would read the cross-chunk `configs` const from
 * lib/platform/config during initialization; if a chunk cycle ever re-forms,
 * that read hits the TDZ and white-screens the SPA. usePlatform() resolves the
 * config lazily at render. The `null` default is only ever used when a consumer
 * renders outside <PlatformProvider> (which never happens), so the lazy
 * fallback keeps behaviour identical.
 */
export const PlatformContext = createContext<PlatformConfig | null>(null);

export function usePlatform(): PlatformConfig {
  return useContext(PlatformContext) ?? getPlatformConfig();
}
